import { FH_TRICKS_PER_HAND } from './config.js';
import { isMisereMode } from './types.js';
export function tricksBySeat(hand, seatCount) {
    const out = Array.from({ length: seatCount }, () => 0);
    for (const t of hand.tricks) {
        if (t.winner !== null && t.winner >= 0 && t.winner < seatCount)
            out[t.winner] += 1;
    }
    return out;
}
function teamTricks(bySeat, table, team) {
    return (table.teams[team]?.seats ?? []).reduce((n, s) => n + (bySeat[s] ?? 0), 0);
}
export function fhScoreHand(hand, table, config) {
    const bySeat = tricksBySeat(hand, table.seatCount);
    const deltas = table.teams.map(() => 0);
    const lines = [];
    const contract = hand.contract;
    if (!contract) {
        // Passed out / abandoned: nobody scores anything (rules §3.1.6).
        return {
            deltas,
            tricksBySeat: bySeat,
            contractMade: null,
            lines: [],
            matchExtra: { lastContract: null },
        };
    }
    const bidTeam = table.teamOf[contract.contractor];
    const misere = isMisereMode(contract.mode);
    const contractorTricks = misere
        ? bySeat[contract.contractor] ?? 0
        : teamTricks(bySeat, table, bidTeam);
    let made;
    let points;
    if (misere) {
        // Rules §6: binary. The partner sat out, so only the contractor's own
        // tricks can break it.
        made = contractorTricks === 0;
        points = made ? contract.value : -contract.value;
        lines.push({
            team: bidTeam,
            text: `${contract.label} ${made ? 'made' : `broken on trick ${contractorTricks}`}`,
            points,
        });
    }
    else {
        made = contractorTricks >= contract.tricks;
        const slam = config.slamBonus &&
            made &&
            contractorTricks === FH_TRICKS_PER_HAND &&
            contract.value < config.slamBonusValue;
        points = made ? (slam ? config.slamBonusValue : contract.value) : -contract.value;
        lines.push({
            team: bidTeam,
            text: slam
                ? `${contract.label} — all 10 tricks, slam bonus`
                : `${contract.label} ${made ? 'made' : `down (${contractorTricks} of ${contract.tricks})`}`,
            points,
        });
    }
    deltas[bidTeam] += points;
    for (const team of table.teams) {
        if (team.id === bidTeam)
            continue;
        // Edge #9: defenders get nothing in a misère contract, regardless of how
        // the contract went.
        const tricks = teamTricks(bySeat, table, team.id);
        const pts = misere ? 0 : tricks * config.defenderTrickPoints;
        deltas[team.id] += pts;
        if (!misere) {
            lines.push({ team: team.id, text: `${tricks} trick${tricks === 1 ? '' : 's'} × ${config.defenderTrickPoints}`, points: pts });
        }
        else if (tricks > 0) {
            lines.push({ team: team.id, text: `${tricks} trick${tricks === 1 ? '' : 's'} — misère pays defenders nothing`, points: 0 });
        }
    }
    return {
        deltas,
        tricksBySeat: bySeat,
        contractMade: made,
        lines,
        matchExtra: {
            lastContract: { contractorSeat: contract.contractor, team: bidTeam, made, label: contract.label },
        },
    };
}
function otherTeamWithBestScore(state, not) {
    let best = null;
    for (const team of state.table.teams) {
        if (team.id === not)
            continue;
        if (best === null || state.scores[team.id] > state.scores[best])
            best = team.id;
    }
    return best;
}
/**
 * Rules §6.1. Order is load-bearing: the bidding side's result is checked
 * first, so when both sides cross the line in the same hand the made contract
 * wins the game (edge #12). Scores are never clamped (edge #13).
 */
export function fhMatchEnd(state, config) {
    const target = config.target;
    const scores = state.scores;
    const last = state.extra?.lastContract ??
        (() => {
            const h = state.handHistory[state.handHistory.length - 1];
            if (!h || h.contractorSeat === null || h.contractMade === null)
                return null;
            return {
                contractorSeat: h.contractorSeat,
                team: state.table.teamOf[h.contractorSeat],
                made: h.contractMade,
                label: h.contractLabel,
            };
        })();
    if (last) {
        // "Going out the back door" — a failed bid can take you straight out.
        if (scores[last.team] <= -target) {
            return {
                finished: true,
                winningTeam: otherTeamWithBestScore(state, last.team),
                reason: `${last.label} failed; fell to ${scores[last.team]}`,
            };
        }
        if (last.made && scores[last.team] >= target) {
            return {
                finished: true,
                winningTeam: last.team,
                reason: `Made ${last.label} to reach ${scores[last.team]}`,
            };
        }
    }
    for (const team of state.table.teams) {
        if (scores[team.id] <= -target) {
            return {
                finished: true,
                winningTeam: otherTeamWithBestScore(state, team.id),
                reason: `Fell to ${scores[team.id]}`,
            };
        }
    }
    // Deliberately NOT an ending: a team can sit above +500 on defenders' trick
    // points indefinitely. Only a made contract wins the game (rules §6.1).
    return { finished: false };
}
//# sourceMappingURL=scoring.js.map